import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Output,
	ViewChild,
} from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	Validators,
} from '@angular/forms';
import { City, Colony, State } from 'src/app/models/catalogs/states.model';
import { ValueDescription } from 'src/app/models/shared/custom-dictionaries';
import { CatalogsService } from 'src/app/services/apis/catalogs.service';

@Component({
	selector: 'cmv-address-form',
	templateUrl: './address-form.component.html',
	styleUrls: ['./address-form.component.scss'],
})
export class AddressFormComponent implements AfterViewInit {
	addressForm: FormGroup;
	isSearchStatesLoading: boolean = false;
	isSearchCityLoading: boolean = false;
	isSearchColonyLoading: boolean = false;
	statesOptions: ValueDescription<number>[] = [];
	citiesOptions: ValueDescription<number>[] = [];
	coloniesOptions: ValueDescription<number>[] = [];
	colonies: Array<Colony>;
	@ViewChild('streetInput') streetInput: ElementRef<HTMLInputElement>;
	@ViewChild('exteriorNumber')
	exteriorNumberInput: ElementRef<HTMLInputElement>;
	@ViewChild('postalCode') postalCodeInput: ElementRef<HTMLInputElement>;

	@Output() addressFormCreated: EventEmitter<FormGroup> = new EventEmitter();
	constructor(
		private formBuilder: FormBuilder,
		private catalogsService: CatalogsService
	) {
		this.initializeForm();
	}

	ngAfterViewInit(): void {
		this.addressFormCreated.emit(this.addressForm);
	}

	get streetField(): AbstractControl {
		return this.addressForm.get('street');
	}
	get interiorNumberField(): AbstractControl {
		return this.addressForm.get('interiorNumber');
	}
	get exteriorNumberField(): AbstractControl {
		return this.addressForm.get('exteriorNumber');
	}
	get knowMyPostalCodeField(): AbstractControl {
		return this.addressForm.get('knowMyPostalCode');
	}
	get postalCodeField(): AbstractControl {
		return this.addressForm.get('postalCode');
	}
	get stateField(): AbstractControl {
		return this.addressForm.get('state');
	}
	get cityField(): AbstractControl {
		return this.addressForm.get('city');
	}
	get colonyField(): AbstractControl {
		return this.addressForm.get('colony');
	}

	onEmmitAddress(): void {
		this.addressFormCreated.emit(this.addressForm);
	}

	onStateChange(selectedState: ValueDescription<number>): void {
		this.colonyField.setValue('');
		this.resetValuesFromCityField();
		this.resetValuesFromPostalCode();
		if (selectedState === null) {
			this.stateField.setValue(null);
			this.stateField.markAsTouched();
			return;
		}

		this.stateField.setValue(selectedState.value);
		this.getCitiesBy(selectedState.value);
	}

	onCityChange(selectedCity: ValueDescription<number>): void {
		this.colonyField.setValue('');
		this.resetValuesFromPostalCode();
		if (selectedCity === null) {
			this.cityField.setValue(null);
			this.cityField.markAsTouched();
			return;
		}

		this.cityField.setValue(selectedCity.value);
		this.getColoniesByStateAndCity(selectedCity.value);
	}

	onColonySelected(selectedColony: ValueDescription<number>) {
		if (selectedColony === null) {
			this.colonyField.setValue(null);
			this.colonyField.markAsTouched();
			return;
		}
		this.colonyField.setValue(selectedColony.value);
		this.postalCodeField.setValue(
			this.colonies.find(colony => colony.colonyId === selectedColony.value)
				.postalCode
		);
	}

	private initializeForm() {
		this.addressForm = this.formBuilder.group({
			street: ['', [Validators.required]],
			exteriorNumber: ['', [Validators.required]],
			interiorNumber: [''],
			knowMyPostalCode: [''],
			postalCode: '',
			state: ['', [Validators.required]],
			city: ['', [Validators.required]],
			colony: ['', [Validators.required]],
		});
		this.getStates();
		this.coloniesOptions = [];
	}

	private resetValuesFromPostalCode(): void {
		this.coloniesOptions = [];
		this.colonyField.setValue('');
	}

	private getStates(): void {
		this.isSearchStatesLoading = true;
		this.catalogsService.getStates().subscribe({
			next: (states: Array<State>) => {
				this.isSearchStatesLoading = false;
				this.statesOptions = states.map(state => ({
					value: state.stateId,
					description: state.description,
				}));
				this.stateField.enable();
			},
			error: (error: Error) => {
				this.isSearchStatesLoading = false;
				console.log(
					'🚀 ~ AddressComponent ~ this.catalogsService.getStates ~ error:',
					error
				);
			},
		});
	}

	private getCitiesBy(idEntidadFederativa): void {
		this.isSearchCityLoading = true;
		this.stateField.disable();
		this.catalogsService.getCities(idEntidadFederativa).subscribe({
			next: (cities: Array<City>) => {
				this.isSearchCityLoading = false;
				this.citiesOptions = cities.map(city => ({
					value: city.cityId,
					description: city.name,
				}));
				this.cityField.setValue('');
				this.coloniesOptions = [];
				this.stateField.enable();
			},
			error: (error: Error) => {
				this.isSearchCityLoading = false;
				this.stateField.enable();
				console.log(
					'🚀 ~ AddressComponent ~ this.catalogsService.getCities ~ error:',
					error
				);
			},
		});
	}

	private getColoniesByStateAndCity(cityId: number): void {
		this.isSearchColonyLoading = true;
		this.stateField.disable();
		this.cityField.disable();
		this.catalogsService.getColonies(cityId).subscribe({
			next: (colonies: Array<Colony>) => {
				this.isSearchColonyLoading = false;
				this.colonies = colonies;
				this.coloniesOptions = colonies.map(colony => ({
					value: colony.colonyId,
					description: colony.name,
				}));
				this.colonyField.setValue('');
				this.stateField.enable();
				this.cityField.enable();
			},
			error: () => {
				this.isSearchColonyLoading = false;
				this.stateField.enable();
				this.cityField.enable();
			},
		});
	}

	private resetValuesFromCityField() {
		this.citiesOptions = [];
		this.cityField.setValue('');
	}
}
